import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState, useRef } from "react"

import LoadingSpinner from "../../../components/shared/LoadingSpinner"
import { formatFontFamily } from "../utils/fontUtils"

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

// Lightweight helper: returns { iana, abbr, longName } for a given IANA id.
// Used to build the cross-timezone banner so clients in a different zone than
// the practitioner don't mistake "7:00am" (shown in the pro's zone) for their
// own local 7:00am — the confusion pattern behind the practice-18635 ticket.
const getZoneInfo = (iana) => {
  if (!iana) return null
  let abbr = iana
  let longName = iana
  try {
    const probe = new Date()
    const shortParts = new Intl.DateTimeFormat("en-US", { timeZone: iana, timeZoneName: "short" }).formatToParts(probe)
    abbr = shortParts.find((p) => p.type === "timeZoneName")?.value || iana
    const longParts = new Intl.DateTimeFormat("en-US", { timeZone: iana, timeZoneName: "long" }).formatToParts(probe)
    longName = longParts.find((p) => p.type === "timeZoneName")?.value || iana
  } catch {
    // Keep defaults; stale/unknown IANA ids fall back to the id itself.
  }
  return { iana, abbr, longName }
}

const CalendarIcon = ({ theme }) => (
  <svg className="size-6" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="4"
      width="18"
      height="18"
      rx="2"
      ry="2"
      stroke={theme?.colors?.textDark || "rgba(85, 67, 63, 1)"}
      strokeWidth="2"
      fill="none"
    />
    <line x1="16" y1="2" x2="16" y2="6" stroke={theme?.colors?.textDark || "rgba(85, 67, 63, 1)"} strokeWidth="2" />
    <line x1="8" y1="2" x2="8" y2="6" stroke={theme?.colors?.textDark || "rgba(85, 67, 63, 1)"} strokeWidth="2" />
    <line x1="3" y1="10" x2="21" y2="10" stroke={theme?.colors?.textDark || "rgba(85, 67, 63, 1)"} strokeWidth="2" />
  </svg>
)

const ServiceAvailableTimes = ({
  availableTimes,
  selectedLocation,
  loading,
  error,
  timeZone,
  onTimeSelect,
  selectedTime,
  theme,
  onLoadMore,
  hasMoreData,
  loadingMore
}) => {
  const [displayedDays, setDisplayedDays] = useState(2)
  const previousLengthRef = useRef(0)
  const loadMoreButtonRef = useRef(null)

  // Show only the first 5 days initially
  const visibleDays = availableTimes.slice(0, displayedDays)

  // Reset displayed days when available times change
  useEffect(() => {
    // Initial load: show first 5 days
    if (availableTimes.length > 0 && displayedDays === 2) {
      setDisplayedDays(5)
      previousLengthRef.current = availableTimes.length
    }
    // When new data is loaded from backend (length increased), show more days automatically
    else if (availableTimes.length > previousLengthRef.current) {
      // Show 5 more days from the newly loaded data
      setDisplayedDays((prev) => Math.min(prev + 5, availableTimes.length))
      previousLengthRef.current = availableTimes.length
    }
  }, [availableTimes.length, displayedDays])

  // Calculate how many months of data we have
  const calculateMonthsLoaded = () => {
    if (!availableTimes || availableTimes.length === 0) return 0

    const firstDate = dayjs(availableTimes[0].date)
    const lastDate = dayjs(availableTimes[availableTimes.length - 1].date)
    const monthsDiff = lastDate.diff(firstDate, "month", true)
    return Math.ceil(monthsDiff) + 1 // +1 to include the starting month
  }

  const monthsLoaded = calculateMonthsLoaded()
  const maxMonths = 6
  const canLoadMoreMonths = monthsLoaded < maxMonths && hasMoreData && onLoadMore

  /** IANA id from bookingAvailability (preferred) or location GraphQL field */
  const displayIana = timeZone || selectedLocation?.formattedTimeZone

  // Client's browser IANA id — used for cross-timezone banner + client-local
  // primary slot labels when offsets differ. Booking still submits the ISO
  // instant; display zone never changes the selected timestamp.
  const clientIana = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || null
    } catch {
      return null
    }
  }, [])

  const proZone = useMemo(() => getZoneInfo(displayIana), [displayIana])
  const clientZone = useMemo(() => getZoneInfo(clientIana), [clientIana])

  // Zones are meaningfully different only when they resolve to a different UTC
  // offset at the time the slots live in. Distinct IANA ids that happen to
  // share the same offset (America/New_York vs America/Toronto) produce no
  // confusion, so we skip the banner for them.
  const zonesDiffer = useMemo(() => {
    if (!displayIana || !clientIana || displayIana === clientIana) return false
    const probeIso = availableTimes?.[0]?.times?.find(
      (t) => typeof t === "string" && (t.includes("T") || t.includes("Z"))
    )
    const probe = probeIso || new Date().toISOString()
    try {
      return dayjs(probe).tz(displayIana).utcOffset() !== dayjs(probe).tz(clientIana).utcOffset()
    } catch {
      return true
    }
  }, [displayIana, clientIana, availableTimes])

  const formatDate = (dateString) => {
    const dateFormat = "dddd, MMMM Do"
    const momentDate = displayIana ? dayjs.tz(dateString, displayIana).startOf("day") : dayjs(dateString).startOf("day")
    const now = displayIana ? dayjs().tz(displayIana).startOf("day") : dayjs().startOf("day")
    const isToday = now.format(dateFormat) === momentDate.format(dateFormat)
    const isTomorrow = now.add(1, "day").format(dateFormat) === momentDate.format(dateFormat)

    if (isToday) {
      return `Today, ${momentDate.format("MMMM D")}`
    } else if (isTomorrow) {
      return `Tomorrow, ${momentDate.format("MMMM D")}`
    } else {
      return momentDate.format(dateFormat)
    }
  }

  const formatTime = (timeString, calendarDate, { iana = displayIana } = {}) => {
    // Convert time string to a readable format like "8:30am", "9:30am"
    if (!timeString) return "Invalid Time"

    try {
      // Full ISO instant from API — show wall clock in the target IANA zone
      // (practice by default; client when zonesDiffer and we pass client IANA).
      if ((timeString.includes("T") || timeString.includes("Z")) && iana) {
        const zoned = dayjs(timeString).tz(iana)
        if (!zoned.isValid()) {
          console.warn(`Invalid time format: ${timeString}`) // eslint-disable-line no-console
          return timeString
        }
        return zoned.format("h:mma")
      }

      // Wall-clock fragments without calendar context — fall back to legacy local parsing
      if ((timeString.includes("T") || timeString.includes("Z")) && !iana) {
        const t = dayjs(timeString)
        return t.isValid() ? t.format("h:mma") : timeString
      }

      if (iana && calendarDate) {
        let wall = timeString
        if (/^\d{1,2}:\d{2}$/.test(wall)) wall = `${wall}:00`
        const parsed = dayjs.tz(`${calendarDate} ${wall}`, "YYYY-MM-DD HH:mm:ss", iana)
        if (parsed.isValid()) return parsed.format("h:mma")
      }

      let time
      if (/^\d{1,2}:\d{2}$/.test(timeString)) {
        time = dayjs(`2000-01-01T${timeString}`)
      } else if (/^\d{1,2}:\d{2}:\d{2}$/.test(timeString)) {
        time = dayjs(`2000-01-01T${timeString}`)
      } else if (/^\d+$/.test(timeString)) {
        const hours = Math.floor(parseInt(timeString, 10) / 60)
        const minutes = parseInt(timeString, 10) % 60
        const formattedHm = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
        time = dayjs(`2000-01-01T${formattedHm}`)
      } else {
        time = dayjs(`2000-01-01T${timeString}`)
      }

      if (!time.isValid()) {
        console.warn(`Invalid time format: ${timeString}`) // eslint-disable-line no-console
        return timeString
      }

      return time.format("h:mma")
    } catch (error) {
      console.error(`Error formatting time "${timeString}":`, error) // eslint-disable-line no-console
      return timeString
    }
  }

  const getTimeZoneLabel = () => {
    const tz = displayIana
    if (!tz) return ""
    let abbr = selectedLocation?.timeZoneAbbr
    try {
      const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(new Date())
      abbr = parts.find((p) => p.type === "timeZoneName")?.value || abbr || tz
    } catch {
      abbr = abbr || tz
    }
    return abbr ? `Times in ${abbr}` : ""
  }

  const handleTimeClick = (date, time) => {
    const selection = { date, time }
    if (onTimeSelect) {
      onTimeSelect(selection)
    }
  }

  const isTimeSelected = (date, time) => selectedTime && selectedTime.date === date && selectedTime.time === time

  const handleLoadMoreTimes = (e) => {
    // Prevent default behavior to avoid any anchor/scroll issues
    e.preventDefault()
    e.stopPropagation()

    // If we have more days already loaded, show more of them
    if (visibleDays.length < availableTimes.length) {
      setDisplayedDays((prev) => prev + 5)
    }
    // If we've shown all loaded days but haven't reached 6 months, load more from backend
    else if (canLoadMoreMonths && onLoadMore) {
      onLoadMore()
    }
  }

  // Determine if we should show the "View more times" button
  const shouldShowLoadMoreButton =
    visibleDays.length < availableTimes.length || // More days already loaded
    (canLoadMoreMonths && onLoadMore) // Can load more months from backend

  // Loading state
  if (loading) {
    return (
      <div className="flex w-full flex-col items-start justify-start gap-8">
        <h2
          className="text-[20px] font-semibold leading-[32px]"
          style={{
            fontFamily: formatFontFamily(theme?.fonts?.paragraph),
            color: theme?.colors?.textDark || "#55433f"
          }}>
          Loading availability...
        </h2>
        <div className="flex w-full justify-center py-16">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex w-full flex-col items-start justify-start gap-8">
        <h2
          className="text-[20px] font-semibold leading-[32px]"
          style={{
            fontFamily: formatFontFamily(theme?.fonts?.paragraph),
            color: theme?.colors?.textDark || "#55433f"
          }}>
          Unable to load availability
        </h2>
        <div className="flex w-full flex-col items-center gap-6 py-16 text-center">
          <div className="text-6xl">🚨</div>
          <p
            className="text-[18px] font-normal leading-[32px]"
            style={{
              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
              color: theme?.colors?.textDark || "#55433f"
            }}>
            Please try again later or contact us for assistance.
          </p>
        </div>
      </div>
    )
  }

  if (!availableTimes || availableTimes.length === 0) {
    if (hasMoreData && onLoadMore) {
      return (
        <div className="flex w-full flex-col items-start justify-start gap-8">
          <h2
            className="text-[20px] font-semibold leading-[32px]"
            style={{
              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
              color: theme?.colors?.textDark || "#55433f"
            }}>
            No available times in this date range
          </h2>
          <p
            className="text-[16px] font-normal leading-[24px]"
            style={{
              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
              color: theme?.colors?.textLight || "#a4786e"
            }}>
            This location may only be open on specific special dates further out. View more times to continue.
          </p>
          <button
            id="view-more-times-button"
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="text-left underline decoration-solid transition-colors hover:no-underline disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              color: theme?.colors?.primary || "#bd654f",
              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
              fontSize: "16px",
              fontWeight: "500"
            }}>
            {loadingMore ? "Loading more availability..." : "View more times"}
          </button>
        </div>
      )
    }

    return (
      <div className="flex w-full flex-col items-start justify-start gap-8">
        <h2
          className="text-[20px] font-semibold leading-[32px]"
          style={{
            fontFamily: formatFontFamily(theme?.fonts?.paragraph),
            color: theme?.colors?.textDark || "#55433f"
          }}>
          No available times
        </h2>
        <div className="flex w-full flex-col items-center gap-6 py-16 text-center">
          <div className="text-6xl">📅</div>
          <p
            className="text-[18px] font-normal leading-[32px]"
            style={{
              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
              color: theme?.colors?.textDark || "#55433f"
            }}>
            Please check back later or contact us to schedule an appointment.
          </p>
        </div>
      </div>
    )
  }

  const timeZoneLabel = getTimeZoneLabel()

  return (
    <div className="flex w-full flex-col items-start justify-start gap-8">
      {/* Cross-timezone banner: shown when the client's browser resolves to a
          different UTC offset than the practitioner's zone. Slot labels use the
          client's local wall clock; practice zone is disclosed here. Checkout
          mirrors the same primary/secondary pairing so the time does not appear
          to "jump" between picker and confirm. */}
      {zonesDiffer && proZone && clientZone && (
        <div
          role="note"
          className="flex w-full items-start gap-3 rounded-md border px-4 py-3"
          style={{
            borderColor: theme?.colors?.primary || "#bd654f",
            backgroundColor: theme?.colors?.bgLight || "#fdf6f3",
            color: theme?.colors?.textDark || "#55433f",
            fontFamily: formatFontFamily(theme?.fonts?.paragraph)
          }}
          data-testid="cross-timezone-banner">
          <div className="text-[18px] leading-none" aria-hidden="true">
            🕐
          </div>
          <div className="text-[14px] leading-[20px]">
            Times below are shown in your local timezone:{" "}
            <strong>
              {clientZone.longName} ({clientZone.abbr})
            </strong>
            .
            <br /> My practice&apos;s timezone is{" "}
            <strong>
              {proZone.longName} ({proZone.abbr})
            </strong>
            .
          </div>
        </div>
      )}

      <div className="flex w-full flex-col gap-6">
        {visibleDays.map((day) => (
          <div key={day.date} className="flex w-full flex-col gap-4">
            {/* Date Header */}
            <div className="flex items-center gap-3">
              <CalendarIcon theme={theme} />
              <h3
                className="text-[18px] font-semibold leading-[28px]"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  color: theme?.colors?.textDark || "#55433f"
                }}>
                {formatDate(day.date)}
              </h3>
              {timeZoneLabel && !zonesDiffer && (
                <span
                  className="text-[14px] font-normal leading-[20px]"
                  style={{
                    fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                    color: theme?.colors?.textLight || "#a4786e"
                  }}>
                  {timeZoneLabel}
                </span>
              )}
            </div>

            {/* Time Slots */}
            <div className="grid grid-cols-6 gap-2 lg:grid-cols-6 md:grid-cols-6 sm:grid-cols-4">
              {day.times.map((time) => {
                const selected = isTimeSelected(day.date, time)
                const proDisplay = formatTime(time, day.date)
                // When the client's browser TZ differs from the practice's zone
                // show only the client-local wall clock — the banner above
                // already discloses the practice's zone.
                const clientDisplay =
                  zonesDiffer && clientZone ? formatTime(time, day.date, { iana: clientZone.iana }) : null
                const primaryDisplay = clientDisplay || proDisplay
                // Zone disclosure lives in the banner and header; keep buttons
                // as bare wall-clock values so a column of slots reads cleanly.
                const accessibleZone = clientDisplay ? clientZone?.abbr : proZone?.abbr
                return (
                  <button
                    key={`${day.date}-${time}`}
                    onClick={() => handleTimeClick(day.date, time)}
                    className={`px-3 py-2 text-center transition-colors ${
                      selected ? "font-semibold" : "hover:bg-gray-50"
                    }`}
                    style={{
                      backgroundColor: selected
                        ? theme?.colors?.primary || "#bd654f"
                        : theme?.colors?.background || "#ffffff",
                      color: selected ? theme?.colors?.textLight || "#FFFFFF" : theme?.colors?.textDark || "#55433f",
                      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                      border: `1px solid ${
                        selected ? theme?.colors?.primary || "#bd654f" : theme?.colors?.border || "#e6e6e6"
                      }`,
                      fontSize: "14px",
                      fontWeight: selected ? "600" : "500",
                      lineHeight: "20px",
                      borderRadius: theme?.buttonShape?.borderRadius || "32px"
                    }}
                    aria-label={accessibleZone ? `${primaryDisplay} ${accessibleZone}` : primaryDisplay}>
                    {primaryDisplay}
                  </button>
                )
              })}
            </div>
          </div>
        ))}

        {/* Load More Button */}
        {shouldShowLoadMoreButton && (
          <div className="flex flex-col gap-4">
            <button
              ref={loadMoreButtonRef}
              id="view-more-times-button"
              type="button"
              onClick={handleLoadMoreTimes}
              disabled={loadingMore}
              className="text-left underline decoration-solid transition-colors hover:no-underline disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                color: theme?.colors?.primary || "#bd654f",
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                fontSize: "16px",
                fontWeight: "500",
                borderRadius: theme?.buttonShape?.borderRadius || "0.375rem"
              }}>
              {loadingMore ? "Loading more availability..." : "View more times"}
            </button>
          </div>
        )}
      </div>

      {/* Divider */}
      <hr
        className="w-full border-t"
        style={{
          borderColor: theme?.colors?.border || "#e6e6e6"
        }}
      />
    </div>
  )
}

ServiceAvailableTimes.propTypes = {
  availableTimes: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      times: PropTypes.arrayOf(PropTypes.string).isRequired
    })
  ).isRequired,
  selectedLocation: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object,
  timeZone: PropTypes.string,
  onTimeSelect: PropTypes.func,
  onLoadMore: PropTypes.func,
  hasMoreData: PropTypes.bool,
  loadingMore: PropTypes.bool,
  selectedTime: PropTypes.object,
  theme: PropTypes.object
}

export default ServiceAvailableTimes
